import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { initialProducts, Product } from '../data/data';
export type { Product };

// Define cart item interface
export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  sku: string;
}

// Define blog post interface
export interface BlogPost {
  id: number;
  title: string;
  category: string;
  categoryUrl: string;
  date: string;
  image: string;
  url: string;
  comments: number;
}

// Define the context type
export interface AppContextType {
  productListSidebar: {
    isVisible: boolean;
    cartItems: CartItem[];
    wishlistItems: CartItem[];
    searchQuery: string;
    selectedCategory: string;
    selectedTag: string;
    priceRange: {
      min: number;
      max: number;
    };
    sortBy: string;
  };
  wishlistModal: {
    isVisible: boolean;
    product: Product | null;
    isRemoval: boolean;
  };
  selectedWishlist: CartItem[];
  selectedPost: BlogPost | null;
  setProductListSidebar: React.Dispatch<React.SetStateAction<{
    isVisible: boolean;
    cartItems: CartItem[];
    wishlistItems: CartItem[];
    searchQuery: string;
    selectedCategory: string;
    selectedTag: string;
    priceRange: {
      min: number;
      max: number;
    };
    sortBy: string;
  }>>;
  setWishlistModal: React.Dispatch<React.SetStateAction<{
    isVisible: boolean;
    product: Product | null;
    isRemoval: boolean;
  }>>;
  setSelectedWishlist: React.Dispatch<React.SetStateAction<CartItem[]>>;
  setSelectedPost: React.Dispatch<React.SetStateAction<BlogPost | null>>;
  products: Product[];
  setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
  filteredProducts: Product[];
  setFilteredProducts: React.Dispatch<React.SetStateAction<Product[]>>;
}

// Create context
export const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

// Initial state
const initialSidebarState = {
  isVisible: false,
  cartItems: [] as CartItem[],
  wishlistItems: [] as CartItem[],
  searchQuery: '',
  selectedCategory: '',
  selectedTag: '',
  priceRange: {
    min: 0,
    max: 1500
  },
  sortBy: 'price'
};

// Provider component
export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [productListSidebar, setProductListSidebar] = useState(initialSidebarState);
  const [products, setProducts] = useState<Product[]>(initialProducts);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(initialProducts);
  const [wishlistModal, setWishlistModal] = useState({
    isVisible: false,
    product: null as Product | null,
    isRemoval: false
  });
  const [selectedWishlist, setSelectedWishlist] = useState<CartItem[]>([]);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  const contextValue: AppContextType = {
    productListSidebar,
    setProductListSidebar,
    wishlistModal,
    setWishlistModal,
    selectedWishlist,
    setSelectedWishlist,
    selectedPost,
    setSelectedPost,
    products,
    setProducts,
    filteredProducts,
    setFilteredProducts
  };

  // Filter products whenever sidebar state or products change
  useEffect(() => {
    const { searchQuery, selectedCategory, selectedTag, priceRange } = productListSidebar;
    let filtered = [...products];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory && selectedCategory !== '' && selectedCategory !== 'all') {
      filtered = filtered.filter(product =>
        product.category === selectedCategory
      );
    }

    // Filter by tag
    if (selectedTag && selectedTag !== '') {
      filtered = filtered.filter(product =>
        product.tags.includes(selectedTag)
      );
    }

    filtered = filtered.filter(product => {
      const productPrice = parseFloat(product.price.replace('₹', '').replace(',', ''));
      return productPrice >= priceRange.min && productPrice <= priceRange.max;
    });

    setFilteredProducts(filtered);
  }, [productListSidebar, products]);

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

// Custom hook to use the context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
